import React, { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	ANALYTICS_BYLINE_EXPERIENCE,
	ANALYTICS_DIALOG_EXPERIENCE,
	NOTIFY_ON_FIRST_VIEW_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { getMark } from '@confluence/performance/entry-points/performance.ts';
import {
	SSR_PARTIAL_COMPONENT,
	SetSSRPartialSuccess,
} from '@confluence/ssr-utilities/entry-points/ssrPartialSuccess.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import { AnalyticsByline as ConfluenceByLine } from '../../base-components/AnalyticsByLine.tsx';
import { SSREvent, useHideByline } from '../../hooks/index.ts';
import { CONTENT_ANALYTICS_DIALOG_METRIC } from '../../perf.config.ts';
import { LazyAnalyticsDialog } from '../AnalyticsDialog/index.ts';
import { DialogPillName } from '../AnalyticsDialog/dialogConstants.tsx';
import { getViewersCount, isExpectedAnalyticsByLineError } from '../analyticsUtils.ts';

import type {
	useAnalyticsByLineNotifyUsersOnFirstViewMutation as useAnalyticsByLineNotifyUsersOnFirstViewMutation$data,
	useAnalyticsByLineNotifyUsersOnFirstViewMutationVariables as useAnalyticsByLineNotifyUsersOnFirstViewMutation$variables,
} from './__types__/useAnalyticsByLineNotifyUsersOnFirstViewMutation.ts';
import { CLICKED_BYLINE_SSR_MARK, TestId } from './constants.ts';
import type { UseAnalyticsByLineHook } from './UseAnalyticsByLineHook.ts';
import type {
	useAnalyticsByLineQuery$data,
	useAnalyticsByLineQuery$variables,
} from './__generated__/useAnalyticsByLineQuery.graphql.ts';
const useAnalyticsByLineQuery = graphql`
	query useAnalyticsByLineQuery($contentId: ID!) {
		contentAnalyticsViewers(contentId: $contentId) {
			count
		}
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				permissionType
			}
		}
	}
`;

export const useAnalyticsByLine: UseAnalyticsByLineHook = ({
	contentId,
	skip,
	wasComponentSSRed,
	contentAuthorId,
	analyticsBylineFragmentData,
}) => {
	const isLivePage = useIsCurrentPageLive();
	const [selectedPill, onPillSelected] = useState<DialogPillName>(DialogPillName.VIEWS);
	const [dialogReferenceDate, setDialogReferenceDate] = useState(new Date());

	const [shouldRefetch, setShouldRefetch] = useState(false);
	const triggerRefetch = useCallback(() => {
		setShouldRefetch(true);
	}, [setShouldRefetch]);

	useEffect(() => {
		setDialogReferenceDate(new Date());
		setShouldRefetch(false);
		onPillSelected(DialogPillName.VIEWS);
	}, [
		contentId,
		// Preserved for compatibility with the AnalyticsByLine implementation
		// before this source code was moved from there to here:
		wasComponentSSRed,
	]);

	const { edition, userId } = useSessionData();

	const experienceTracker = useContext(ExperienceTrackerContext);
	const shouldUseAnalyticsBylineFragment = Boolean(
		!skip &&
		analyticsBylineFragmentData &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_analytics_byline_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false),
	);
	const {
		data: queryData,
		loading: queryLoading,
		error,
		refetch,
	} = useQueryInterop<useAnalyticsByLineQuery$data, useAnalyticsByLineQuery$variables>(
		useAnalyticsByLineQuery,
		{
			variables: {
				contentId,
			},
			context: {
				single: true,
			},
			skip: skip || shouldUseAnalyticsBylineFragment,
		},
	);
	const data =
		(shouldUseAnalyticsBylineFragment ? analyticsBylineFragmentData : queryData) ?? undefined;
	const loading = shouldUseAnalyticsBylineFragment ? false : queryLoading;

	useEffect(() => {
		if (
			error &&
			isExpectedAnalyticsByLineError(error) &&
			expValEquals('cc-reduce-js-error-rate-batch1', 'isEnabled', true)
		) {
			markErrorAsHandled(error);
		}
	}, [error]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	// mutations sends request to BE to notify users of content first view
	// content owner will receive notification on first view in bell icon
	const [notifyUsersOnFirstView, { error: notifyUsersOnFirstViewError }] = useMutationInterop<
		useAnalyticsByLineNotifyUsersOnFirstViewMutation$data,
		useAnalyticsByLineNotifyUsersOnFirstViewMutation$variables
	>(graphql`
		mutation useAnalyticsByLineNotifyUsersOnFirstViewMutation($input: ID!) {
			notifyUsersOnFirstView(contentId: $input) {
				success
				errors {
					message
				}
			}
		}
	`);
	const notifyUsersOnFirstViewMutationStorageKey = `${contentId}/${keys.NOTIFY_USERS_ON_FIRST_VIEW_MUTATION_FIRED}`;

	const handleNotify = useCallback(async () => {
		experienceTracker.start({ name: NOTIFY_ON_FIRST_VIEW_EXPERIENCE });
		try {
			await notifyUsersOnFirstView({ variables: { input: contentId } });

			experienceTracker.succeed({ name: NOTIFY_ON_FIRST_VIEW_EXPERIENCE });

			sessionStorage.setItem(notifyUsersOnFirstViewMutationStorageKey, true);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'succeeded',
					actionSubject: 'FirstViewNotification',
					source: 'viewPageScreen',
					objectId: contentId,
					attributes: {
						contentId,
					},
				},
			}).fire();
		} catch (err) {
			// handle error
			experienceTracker.fail({
				error: err || new Error('An unknown error has occurred'),
				name: NOTIFY_ON_FIRST_VIEW_EXPERIENCE,
			});
		}
	}, [
		contentId,
		createAnalyticsEvent,
		experienceTracker,
		notifyUsersOnFirstView,
		notifyUsersOnFirstViewMutationStorageKey,
	]);

	// We want to allow the data to refresh once upon opening the dialog for the first time, but never
	// beyond that. This one-time refresh exists so that the data displayed in the dialog matches the
	// data being displayed in the byline.
	const hasRefetched = useRef(false);

	useEffect(() => {
		if (shouldRefetch && !hasRefetched.current) {
			void refetch({ contentId });

			hasRefetched.current = true;
		} else if (!shouldRefetch && hasRefetched.current) {
			hasRefetched.current = false;
		}
	}, [contentId, refetch, shouldRefetch]);

	const count = getViewersCount(data);

	const hideAnalyticsByLine = useHideByline(loading, data, error) || count < 2;
	const isPageAuthor = userId === contentAuthorId;

	// Trigger notification if 1 page view, paid editions, english locale, & experiment enabled
	useEffect(() => {
		if (
			count === 1 &&
			// when user publish page without hard-reload page, the page viewer count doesn't get updated
			// adding this check to avoid duplicate calls made to complement `count === 1` check to avoid unnecessary duplicate calls in `handleNotify`
			!sessionStorage.doesContain(notifyUsersOnFirstViewMutationStorageKey) &&
			edition !== ConfluenceEdition.FREE &&
			!isPageAuthor &&
			fg('cc_onboarding_first_view_notify_prod')
		) {
			void handleNotify();
		}
	}, [count, handleNotify, edition, isPageAuthor, notifyUsersOnFirstViewMutationStorageKey]);
	const onPopupTriggerSSREvent = useCallback(
		(event: SSREvent) => {
			switch (event) {
				case SSREvent.CLICK:
					CONTENT_ANALYTICS_DIALOG_METRIC.start({
						startTime: getMark(CLICKED_BYLINE_SSR_MARK),
					});
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'clicked',
							actionSubject: 'AnalyticsButton',
							source: 'viewPageScreen',
							objectId: contentId,
							attributes: {
								from: 'fromByline',
								isSSR: wasComponentSSRed,
								isLivePage,
							},
						},
					}).fire();
					break;

				case SSREvent.FOCUS:
				case SSREvent.HOVER:
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'hovered',
							actionSubject: 'AnalyticsButton',
							source: 'page',
							objectId: contentId,
							attributes: {
								from: 'fromByline',
								isSSR: wasComponentSSRed,
								tabFocus: event === SSREvent.FOCUS,
								isLivePage,
							},
						},
					}).fire();
					break;
			}
		},
		[contentId, createAnalyticsEvent, wasComponentSSRed, isLivePage],
	);

	return {
		hideAnalyticsByLine,
		error: error ?? notifyUsersOnFirstViewError,
		loading,

		//
		// Popup content
		//

		popupContent: ({ onClose, zIndex }) => (
			<LazyAnalyticsDialog
				contentId={contentId}
				onClose={onClose}
				referenceDate={dialogReferenceDate}
				selectedPill={selectedPill}
				onPillSelected={onPillSelected}
				requestBylineCountRefetch={triggerRefetch}
				zIndex={zIndex}
			/>
		),

		popupContentExperience: ANALYTICS_DIALOG_EXPERIENCE,

		onPopupContentClose: () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'dismissed',
					actionSubject: 'analyticsPageModal',
					actionSubjectId: 'analytics',
					objectId: contentId,
					source: 'analyticsPageModal',
				},
			}).fire();
		},

		//
		// Popup trigger
		//

		popupTrigger: ({ onClick, 'aria-expanded': isOpen }) =>
			hideAnalyticsByLine || (
				<>
					<ConfluenceByLine
						contentId={contentId}
						count={count}
						isOpen={isOpen === true}
						onClick={onClick}
						isBylineComponent
						abbreviatePageViews
						useBadgeStyle
						testId={TestId.BYLINE}
					/>
					<SetSSRPartialSuccess name={SSR_PARTIAL_COMPONENT.AnalyticsByLine} />
				</>
			),

		popupTriggerExperience: ANALYTICS_BYLINE_EXPERIENCE,

		onPopupTriggerSSREvent,
	};
};
